import { editPhoneDraft, formatPhoneDraft } from './phoneDraft'
import type { TextInput as RNTextInput } from 'react-native'
import { Pressable, StyleSheet, TextInput } from 'react-native'

import { FigmaChevronDownIcon } from '../icons/FigmaSystemIcons'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { inputTextStyle } from '../theme/inputTextStyle'
import { Box, Text, useTheme } from '../theme/restyle'
import { KYC_FORM_METRICS } from './kycFormTheme'
import { type PhoneCountry, countryFlag, countryName } from './phoneCountries'

export interface FiatPhoneFieldProps {
  country: PhoneCountry
  /** Raw input under the country code, including any leading trunk digit. */
  national: string
  /** Whether the country list is up, so the chip can say so. */
  isOpen: boolean
  editable?: boolean
  autoFocus: boolean
  /** `undefined` where the pad has no return key to give. */
  returnKeyType?: 'next' | 'done'
  onChangeNational: (national: string) => void
  onOpenCountries: () => void
  onSubmitEditing: () => void
  /** Focusing the digits is choosing them over the list the chip raised. */
  onFocus: () => void
  onLayoutRef: (input: RNTextInput | null) => void
}

/**
 * A phone number as one field: the calling code chosen on the left, the rest
 * typed on the right.
 *
 * The rail takes E.164 and nothing else, so the code cannot be the user's to
 * remember — the old copy asked them to type it and a local number left
 * Continue dead with no explanation.
 */
export function FiatPhoneField({
  country,
  national,
  isOpen,
  autoFocus,
  editable = true,
  returnKeyType,
  onChangeNational,
  onOpenCountries,
  onSubmitEditing,
  onFocus,
  onLayoutRef,
}: FiatPhoneFieldProps) {
  const { t, locale } = useFunkitTranslation()
  const theme = useTheme()
  const display = formatPhoneDraft(country, national)

  return (
    <Box
      flexDirection="row"
      alignItems="center"
      style={[styles.field, { backgroundColor: theme.form.field }]}
    >
      <Pressable
        onPress={onOpenCountries}
        disabled={!editable}
        accessibilityRole="button"
        accessibilityLabel={t('fiat.phoneCountryLabel')}
        accessibilityValue={{
          text: `${countryName(country, locale)} ${country.dialCode}`,
        }}
        accessibilityState={{ expanded: isOpen }}
      >
        <Box flexDirection="row" alignItems="center" style={styles.chip}>
          <Text style={styles.flag}>{countryFlag(country.alpha2)}</Text>
          <Text
            weight="medium"
            style={[
              styles.countryCode,
              { color: theme.content.elements.primary },
            ]}
          >
            {country.dialCode}
          </Text>
          <FigmaChevronDownIcon
            size={15}
            color={theme.content.elements.secondary}
          />
        </Box>
      </Pressable>

      {/* The chip is part of the same box, so a rule is what tells the two
          halves apart — the same one a grouped card rules its rows with. */}
      <Box style={[styles.divider, { backgroundColor: theme.form.divider }]} />

      <TextInput
        ref={onLayoutRef}
        value={display}
        onChangeText={(next) => onChangeNational(editPhoneDraft(display, next))}
        placeholder={t('fiat.phonePlaceholder')}
        placeholderTextColor={theme.content.elements.tertiary}
        keyboardType="number-pad"
        textContentType="telephoneNumber"
        autoComplete="tel"
        autoCapitalize="none"
        autoCorrect={false}
        spellCheck={false}
        autoFocus={autoFocus}
        editable={editable}
        returnKeyType={returnKeyType}
        onSubmitEditing={onSubmitEditing}
        onFocus={onFocus}
        style={[
          inputTextStyle(theme),
          theme.textWeights.medium,
          styles.input,
          { color: theme.content.elements.primary },
        ]}
      />
    </Box>
  )
}

const PHONE_FIELD_METRICS = {
  /** Between the flag, the code and the chevron. */
  chipGap: 6,
  /** Large enough to read as a flag beside body-sized type. */
  flagFontSize: 17,
  /** The rule is shorter than the box, as a card's rules are. */
  dividerHeight: 34,
}

const styles = StyleSheet.create({
  field: {
    height: KYC_FORM_METRICS.fieldHeight,
    borderRadius: KYC_FORM_METRICS.fieldRadius,
  },
  // The chip pays the box's inset on the left; the input pays it on the right.
  chip: {
    height: KYC_FORM_METRICS.fieldHeight,
    paddingHorizontal: KYC_FORM_METRICS.fieldPaddingHorizontal,
    gap: PHONE_FIELD_METRICS.chipGap,
  },
  countryCode: { fontSize: 17, letterSpacing: 1.02 },
  flag: { fontSize: PHONE_FIELD_METRICS.flagFontSize },
  divider: {
    width: StyleSheet.hairlineWidth,
    height: PHONE_FIELD_METRICS.dividerHeight,
  },
  // Undo the platform's own padding so the field's height is the box's.
  input: {
    flex: 1,
    padding: 0,
    paddingLeft: 11,
    paddingRight: KYC_FORM_METRICS.fieldPaddingHorizontal,
    fontSize: 17,
    letterSpacing: -0.425,
  },
})
